
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import httpClient from "../../store/httpClient";
import TicketPosition from "./TicketPosition.vue";
import { BlexonResolutionType } from "../../dtos/blexonResolutionType";
import { TicketDto } from "../../dtos/ticketDto";
import TransportSettings from "./TransportSettings.vue";
import ReturnTransportSettings from "./ReturnTransportSettings.vue";
import CostDistribution from "./CostDistribution.vue";
import { TicketState } from "@/dtos/ticketState";

@Component({
	components: {
		TransportSettings,
		ReturnTransportSettings,
		TicketPosition,
		CostDistribution,
	},
})
export default class ProcessTicket extends Vue {
	@Prop()
	private ticketId!: string;
	private ticketDto: TicketDto | null = null;
	private currentPosition: number = 0;
	private loading: boolean = true;
	private costDistributionIsValid: boolean = false;
	private nacharbeitTransportExists: boolean = false;
	private nacharbeitValid: boolean = false;
	private nacharbeitTransportWarningDialog: boolean = false;

	private async mounted() {
		await this.loadTicket();
	}

	private get showCostDistribution() {
		if (this.ticketDto == null) {
			return false;
		}
		return this.ticketDto.complains.every((c) => c.blexonResolution > 0);
	}

	private get costDistributionTabIndex(): number {
		if (this.ticketDto == null) return -1;
		return this.ticketDto.complains.length;
	}

	@Watch("currentPosition")
	private updateTransportCost() {
		if (this.currentPosition === this.costDistributionTabIndex) {
			if (this.showCostDistribution) {
				(this.$refs.costDistribution as any).calculateCost();
			}
		}
	}

	private onResolutionChanged() {
		if (this.showCostDistribution) {
			(this.$refs.costDistribution as any).clearPreviousCalculation();
		}
		if (this.isNacharbeitIntended && this.$refs.returnTransport) {
			(this.$refs.returnTransport as any).checkValid();
		}
	}

	private get isNacharbeitIntended() {
		if (this.ticketDto == null) {
			return false;
		}
		return this.ticketDto.complains.some(
			(c) => c.blexonResolution === BlexonResolutionType.AcceptedNacharbeit
		);
	}

	private get isNacharbeitValid() {
		if (!this.isNacharbeitIntended) return true;
		return this.nacharbeitValid;
	}

	private get isTransportNeeded() {
		if (this.ticketDto == null) {
			return false;
		}
		return this.ticketDto.complains.some(
			(c) =>
				c.blexonResolution === BlexonResolutionType.AcceptedNacharbeit ||
				c.blexonResolution === BlexonResolutionType.AcceptedErsatzlieferung
		);
	}

	private async loadTicket() {
		try {
			this.loading = true;
			var response = await httpClient().get(
				`Ticket/GetTicketProcessing?ticketId=${this.ticketId}`
			);
			this.ticketDto = response.data;
		} catch {
			console.log("Error");
		} finally {
			this.loading = false;
		}
	}

	private async checkProcessTicket() {
		if (!this.ticketDto) return;
		if (this.$refs.costDistribution as any) {
			(this.$refs.costDistribution as any).validate();
		}
		if (!this.costDistributionIsValid) {
			this.$store.commit("setSnackbarErrorText", "Kostenverteilung ist ungültig.");
			return;
		}
		// show warning dialog if Nacharbeit is intended but no transport exists yet
		if (this.isNacharbeitIntended && !this.nacharbeitTransportExists) {
			this.nacharbeitTransportWarningDialog = true;
			return;
		} else {
			this.processTicket();
		}
	}

	private async processTicket() {
		if (!this.ticketDto) return;
		try {
			this.loading = true;
			var response = await httpClient().post(
				`Ticket/processTicket?ticketId=${this.ticketId}`
			);
			this.ticketDto.state = TicketState.Resolved;
			this.$store.commit("setSnackbarText", response.data);
			this.$router.push({ name: "ticket" });
		} catch {
			console.log("Error");
		} finally {
			this.loading = false;
		}
	}

	private tabChanged() {
		if (this.$refs.costDistribution as any) {
			(this.$refs.costDistribution as any).validate();
		}
	}
}
